'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Pathname } from '@pioz/mlm-common/types';
import { CommonLocals, getPathnameWithoutLocale } from '@pioz/mlm-common';

declare global {
  interface Window {
    FreshworksWidget: FreshworksWidgetType;
    fwSettings: {
      widget_id: string;
      locale?: string;
    };
  }
}

type FreshworksWidgetType = ((
  command?: string,
  action?: string | LocalizationType,
  data?: unknown,
) => void) & {
  q: unknown[];
};

interface LocalizationType {
  [key: string]: {
    launcher: string;
    banner: string;
    contact_form: {
      title: string;
      name: string;
      email: string;
      subject: string;
      description: string;
      submit: string;
      confirmation: string;
    };
  };
}

interface UserInterface {
  email?: string;
  name?: string;
}

// Documentation: https://developers.freshdesk.com/widget-api/#introduction
export function FreshdeskWidgetClient({
  locale,
  freshKey,
  user,
  userReadOnly = false,
  shownOnlyOn,
  adminUser,
  t,
}: {
  locale: CommonLocals;
  freshKey: string;
  user?: UserInterface;
  userReadOnly?: boolean;
  shownOnlyOn?: Pathname[];
  adminUser: boolean;
  t: LocalizationType[0];
}) {
  const hideOn: Pathname[] = ['/checkout/'];
  const [initialized, setInitialized] = useState(false);
  const pathname = usePathname() as Pathname;

  const pathnameWithoutLocale = getPathnameWithoutLocale({ pathname, locales: [locale] });

  const showWidgetButton = !adminUser
    ? shownOnlyOn
      ? shownOnlyOn.includes(pathnameWithoutLocale)
      : !hideOn.includes(pathnameWithoutLocale)
    : false;

  useEffect(() => {
    if (!showWidgetButton) return;

    let scriptElement: HTMLScriptElement | null = null;
    let scriptSrcElement: HTMLScriptElement | null = null;

    setTimeout(() => {
      const scriptContent = `
        window.fwSettings = {
          'widget_id': ${extractWidgetId(freshKey)},
        };
        !function() {
          if ("function" != typeof window.FreshworksWidget) {
            var n = function() { n.q.push(arguments) };
            n.q = [];
            window.FreshworksWidget = n;
          }
        }();
      `;

      scriptElement = document.createElement('script');
      scriptElement.innerHTML = scriptContent;
      document.body.appendChild(scriptElement);

      let scriptSrcElement = document.createElement('script');
      scriptSrcElement.src = freshKey;
      scriptSrcElement.async = true;
      scriptSrcElement.defer = true;
      document.body.appendChild(scriptSrcElement);

      setInitialized(true);
    }, 3000);

    return () => {
      if (scriptElement) document.body.removeChild(scriptElement);
      if (scriptSrcElement) document.body.removeChild(scriptSrcElement);
    };
  }, [freshKey]);

  useEffect(() => {
    if (!showWidgetButton || !initialized) return;

    const prefillUser = (user: UserInterface, userReadOnly: boolean) => {
      window.FreshworksWidget('identify', 'ticketForm', user);

      if (userReadOnly) {
        window.FreshworksWidget('disable', 'ticketForm', ['email', 'name']);
      }
    };

    const setLabels = (t: LocalizationType[0]) => {
      window?.FreshworksWidget('setLabels', {
        en: t,
      });
    };

    if (!window?.FreshworksWidget) return;

    setTimeout(() => {
      setLabels(t);

      if (user) {
        prefillUser(user, userReadOnly);
      }
    }, 10000);
  }, [initialized, freshKey, user, userReadOnly, t]);

  useEffect(() => {
    if (!initialized) return;

    function manageVisibility() {
      const widgetPresent = !!window?.FreshworksWidget;

      if (!showWidgetButton && widgetPresent) {
        window.FreshworksWidget?.('hide');
      } else if (widgetPresent) {
        window.FreshworksWidget?.('show');
      }
    }

    manageVisibility();
  }, [showWidgetButton, initialized]);

  return null;
}

function extractWidgetId(url: string): string | undefined {
  const match = url.match(/\/widgets\/(\d+)\.js/);
  return match ? match[1] : undefined;
}
